import { PlusOutlined } from '@ant-design/icons';
import { Select, Tag, Tooltip } from 'antd';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Flex, Icon as UIIcon } from 'ui';

const { Option } = Select;

const StyledSelect = styled(Select)`
  height: 22px;
  width: ${p => p.width}px !important;
  .ant-select-selection {
    line-height: 0 !important;
    height: 22px !important;
  }
  .ant-select-search {
    line-height: 0 !important;
    height: 20px !important;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  .ant-tag {
    margin: 3px;
  }
`;

const SelectedIcon = styled(UIIcon)`
  margin-left: 10px;
`;

const StyledFlex = styled(Flex)`
  ${p => p.isSelected && 'background-color: #fafafa'};
  margin: -5px -12px;
  padding: 5px 12px
`;

const StyledTag = styled(Tag)`
  background: transparent;
  border-style: dashed;
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.65)'};
  border-color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.65)'};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const List = styled.ul`
  list-style: none;
  padding-left: 0px;
`;

const ListItem = styled.li`
  .ant-tag {
    margin: 3px 0;
  }
`;

const Tags = ({
  selectedTags,
  name,
  resetTags,
  form,
  categoryInfo,
  isNew,
  ticket,
  allCategoryTags,
  allTags,
  setFormDirty,
  intl,
  tagTextColor,
  newTagText,
  addTagCallback,
  removeTagCallback,
  initialSelectedTags,
  hideOverflow = false,
  showFullDescription = false,
  disabled = false,
  addIdToDescription = true,
  inputWidth,
  useBodyAsParent = false,
  dropdownPlacement = 'bottomLeft',
  maxItemsToShow = 4,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputVisible, setInputVisible] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [allSelectedTags, setAllSelectedTags] = useState(selectedTags);

  useEffect(() => {
    if (selectedTags && allSelectedTags && !isEqual(selectedTags, allSelectedTags)) {
      setAllSelectedTags(selectedTags);
    }
  }, [selectedTags]);

  useEffect(() => {
    if (resetTags) {
      setAllSelectedTags(initialSelectedTags);
    }
  }, [resetTags]);

  const updateForm = (value) => {
    if (form) {
      form.setFieldsValue({
        [name]: value,
      });
    }
  };

  useEffect(() => {
    if (categoryInfo) {
      if (isNew || !ticket.categoryId) {
        updateForm([]);
        setAllSelectedTags([]);
      }
    }
  }, [categoryInfo]);

  useEffect(() => {
    if (allSelectedTags) {
      updateForm(allSelectedTags);
      if (hideOverflow) {
        setShowOverflow(allSelectedTags.length > maxItemsToShow);
      }
    }
  }, [allSelectedTags]);

  const handleInputConfirm = (e) => {
    const isSelected = allSelectedTags?.find(s => s.id === e);
    if (!isSelected) {
      if (addTagCallback) {
        addTagCallback(e);
      } else {
        const newTag = allCategoryTags?.find(ct => ct.id === e);
        setAllSelectedTags(tags => [...tags, newTag]);
      }
    } else if (removeTagCallback) {
      const removedTag = allCategoryTags?.find(ct => ct.id === e);
      removeTagCallback(e, removedTag?.questionId);
    } else {
      setAllSelectedTags(tags => tags?.filter(t => t.id !== e));
    }
    if (setFormDirty) {
      setFormDirty();
    }
    setInputVisible(false);
    setInputValue('');
  };

  const handleBlur = () => {
    setInputVisible(false);
    setInputValue('');
  };

  const handleInputChange = (e) => {
    setInputValue(e);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleClose = (e) => {
    const removedTag = allTags?.find(ct => ct.id === e);
    setAllSelectedTags(tags => tags?.filter(t => t.id !== e));
    if (removeTagCallback) {
      removeTagCallback(e, removedTag?.questionId);
    }
    if (setFormDirty) {
      setFormDirty();
    }
  };

  const renderTag = (tag, limit, inOverflowTooltip = false) => {
    const tagDescription = `${tag.description}${addIdToDescription ? ` (${tag.id})` : ''}`;
    const isLongTag = tagDescription?.length > limit;
    const tagElem =
      <Tag
        key={tag.id}
        closable={!disabled}
        onClose={() => handleClose(tag.id)}
        color={tagTextColor}
      >
        {isLongTag && !showFullDescription ?
          `${tagDescription.slice(0, limit)}...` :
          tagDescription
        }
      </Tag>;
    return isLongTag && !showFullDescription ?
      <Tooltip
        title={tagDescription}
        key={tag.id}
        getPopupContainer={node => !inOverflowTooltip ? node.parentNode : document.body}
      >
        {tagElem}
      </Tooltip>
      :
      tagElem;
  };

  const overFlowTags = <List>
    {allSelectedTags
      ?.filter((t, i) => i >= maxItemsToShow)
      ?.map(tag =>
        <ListItem key={tag.id}>
          {renderTag(tag, 50, true)}
        </ListItem>,
      )}
  </List>;

  return (
    <Wrapper>
      {allSelectedTags
        ?.filter((t, i) => hideOverflow ? i < maxItemsToShow : true)
        ?.map(t => renderTag(t, 20, false))
      }
      {showOverflow ?
        <Tooltip
          title={overFlowTags}
          key='overflow-tooltip'
          getPopupContainer={triggerNode => triggerNode.parentNode}
          placement='bottom'
          color='white'
          overlayInnerStyle={{ width: 'fit-content' }}
        >
          <Tag
            key='overflow-tag'
            closable={false}
            color={tagTextColor}
          >
            <UIIcon type='ellipsis-h' />
          </Tag>
        </Tooltip> :
        null
      }
      {inputVisible &&
        <StyledSelect
          value={inputValue}
          filterOption={(value, option) =>
            // Children of the option is the StyledFlex, the children of that styledflex are
            // the div and the selected icon, so we need to search on the children of the
            // first div (hence the [0]) and not on the icon
            option?.props?.children?.props?.children[0]?.props?.children
              ?.toString()
              ?.toLowerCase()
              ?.includes(value?.toLowerCase())
          }
          width={inputWidth || 178}
          dropdownMatchSelectWidth={false}
          getPopupContainer={triggerNode => useBodyAsParent ?
            document.body :
            triggerNode.parentNode.parentNode
          }
          size='small'
          onSearch={handleInputChange}
          onSelect={handleInputConfirm}
          onBlur={handleBlur}
          showArrow={false}
          showSearch={true}
          defaultOpen={true}
          autoFocus={true}
          placement={dropdownPlacement}
          dropdownStyle={{ maxWidth: 750 }}
        >
          {/* Only show tags that don't have a related question or
        if they have one, make sure that the question isn't already answered */}
          {allCategoryTags
            ?.filter(t =>
              !t.questionId ||
              !allSelectedTags?.find(s => s.questionId === t.questionId && s.id !== t.id),
            )
            ?.map((d) => {
              const isSelected = allSelectedTags?.find(s => s.id === d.id);
              return (
                <Option disabled={disabled} key={d.id}>
                  <StyledFlex ffrn jcsb aic isSelected={isSelected}>
                    <div>{`${d.description}${addIdToDescription ? ` (${d.id})` : ''}`}</div>
                    {isSelected ? <SelectedIcon type='check' size='md' /> : null}
                  </StyledFlex>
                </Option>
              );
            },
            )}
        </StyledSelect>
      }
      {!inputVisible &&
        <StyledTag onClick={!disabled ? showInput : null} disabled={disabled}>
          <PlusOutlined />{' '}
          <span>{newTagText || intl.formatMessage({ id: 'ticket.NEW_TAG' })}</span>
        </StyledTag>
      }
    </Wrapper>
  );
};

Tags.propTypes = {
  selectedTags: PropTypes.array,
  resetTags: PropTypes.func,
  form: PropTypes.object,
  categoryInfo: PropTypes.object,
  isNew: PropTypes.bool,
  ticket: PropTypes.object,
  allCategoryTags: PropTypes.array,
  allTags: PropTypes.array,
  setFormDirty: PropTypes.func,
  intl: PropTypes.object,
  name: PropTypes.string,
  tagTextColor: PropTypes.string,
  newTagText: PropTypes.string,
  addTagCallback: PropTypes.func,
  removeTagCallback: PropTypes.func,
  initialSelectedTags: PropTypes.array,
  hideOverflow: PropTypes.bool,
  showFullDescription: PropTypes.bool,
  disabled: PropTypes.bool,
  addIdToDescription: PropTypes.bool,
  inputWidth: PropTypes.number,
  useBodyAsParent: PropTypes.bool,
  dropdownPlacement: PropTypes.string,
  maxItemsToShow: PropTypes.number,
};

export default injectIntl(Tags);
